<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container fluid>
        <!-- Desktop toolbar (hidden below lg — the search col + both
             full-text buttons don't fit at md, so they'd wrap below the
             search bar; the compact icon toolbar below covers md and down) -->
        <b-row
          align-v="center"
          class="mt-3 justify-content-between d-none d-lg-flex"
        >
          <b-col md="5" lg="4" class="mb-1 mb-md-0">
            <b-form-input
              v-model="search"
              placeholder="Search project name..."
              debounce="500"
            />
          </b-col>
          <b-col cols="auto" class="d-flex align-items-center px-md-2">
            <b-button
              v-if="canCreateProject"
              variant="primary"
              class="mr-2 action-btn"
              @click="addingProject(true)"
            >
              <b-icon-plus class="align-middle"></b-icon-plus>
              <span class="align-middle"> Add New Project</span>
            </b-button>
            <b-button
              v-if="canExportMatrix"
              variant="success"
              class="action-btn"
              @click="downloadAllTicketsCsvSquashed"
            >
              <b-icon-download class="align-middle" />
              <span class="align-middle"> Export Tradie Matrix</span>
            </b-button>
          </b-col>
        </b-row>

        <!-- Compact icon toolbar (md and down — see note above) -->
        <div
          class="d-lg-none d-flex mt-3"
          style="gap: 0.4rem; align-items: stretch"
        >
          <b-form-input
            v-model="search"
            placeholder="Search project..."
            debounce="500"
            class="flex-grow-1 project-mobile-search"
          />
          <b-button
            v-if="canCreateProject"
            variant="primary"
            class="project-mobile-btn"
            @click="addingProject(true)"
          >
            <b-icon-plus class="align-middle" />
          </b-button>
          <b-button
            v-if="canExportMatrix"
            variant="success"
            class="project-mobile-btn"
            @click="downloadAllTicketsCsvSquashed"
          >
            <b-icon-download class="align-middle" />
          </b-button>
        </div>

        <!-- Mobile: compact project rows (hidden on md+) -->
        <div class="d-md-none pb-2 mt-2">
          <div
            v-for="item in projects"
            :key="item.id"
            class="mobile-project-item"
            :class="canViewProjectDetail ? 'c-pointer' : ''"
            @click="viewProject(item)"
          >
            <div class="d-flex align-items-center">
              <div class="flex-grow-1" style="min-width: 0">
                <span class="mobile-project__name">{{ item.name || '-' }}</span>
                <div class="mobile-project__meta">
                  {{ item.tradies_count || 0 }} tradies<template
                    v-if="item.tradies_need_attention_count"
                  >
                    ·
                    {{ item.tradies_need_attention_count }} attention</template
                  ><template v-if="item.project_approved_tradies_count != null">
                    ·
                    {{ item.project_approved_tradies_count }} approved</template
                  >
                </div>
              </div>
              <div
                v-if="canArchiveProject"
                class="ml-2 flex-shrink-0"
                @click.stop
              >
                <span
                  v-b-tooltip.hover
                  :title="
                    isShareLocked(item) ? 'Revoke shares before archiving.' : ''
                  "
                  class="d-inline-block"
                >
                  <b-button
                    :variant="cellVariant"
                    :disabled="isShareLocked(item)"
                    :title="cellText"
                    class="mobile-project__archive-btn"
                    @click.stop="archiveProject(item)"
                  >
                    <b-icon
                      :icon="
                        showArchived ? 'arrow-counterclockwise' : 'archive'
                      "
                      class="align-middle"
                    />
                  </b-button>
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="!projects || !projects.length"
            class="text-center py-4 text-muted"
          >
            No projects found.
          </div>
        </div>

        <!-- Desktop: full table (hidden below md) -->
        <div class="d-none d-md-block">
          <b-table
            id="my-table"
            class="mt-4"
            responsive
            hover
            show-empty
            ref="projectsTable"
            :fields="fields"
            :items="projects"
            :tbody-tr-class="canViewProjectDetail ? 'cursor-pointer' : ''"
            @row-clicked="viewProject"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <digger-loader />
              </div>
            </template>
            <template #cell(action)="slot">
              <template v-if="canArchiveProject">
                <span
                  v-b-tooltip.hover
                  :title="
                    isShareLocked(slot.item)
                      ? 'Revoke shares before archiving this project.'
                      : ''
                  "
                  class="d-inline-block"
                >
                  <b-button
                    pill
                    size="sm"
                    :variant="cellVariant"
                    :disabled="isShareLocked(slot.item)"
                    class="h-100"
                    @click="archiveProject(slot.item)"
                  >
                    {{ cellText }}
                  </b-button>
                </span>
              </template>
            </template>
          </b-table>
        </div>

        <b-pagination
          v-if="rows > perPage"
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :current-page="currentPage"
          aria-controls="my-table"
          align="center"
        />

        <p v-if="projectsMeta" align="center">
          Showing {{ projects.length }} out of total of
          {{ projectsMeta.total }} projects
        </p>

        <!-- Dialog - Add Project -->
        <b-modal
          v-model="isAddingProject"
          title="Add New Project"
          centered
          :hide-footer="true"
        >
          <b-form @submit.prevent="onAddProject">
            <b-form-group label="Project Name">
              <b-form-input
                v-model="project.name"
                placeholder="Enter Project Name"
                required
              ></b-form-input>
            </b-form-group>

            <div class="add-project-actions d-flex pt-2">
              <!-- Cancel -->
              <b-button
                variant="outline-secondary"
                class="modal-btn"
                @click="isAddingProject = false"
              >
                CANCEL
              </b-button>

              <!-- Submit -->
              <b-button type="submit" variant="primary" class="modal-btn">
                SUBMIT
              </b-button>
            </div>
          </b-form>
        </b-modal>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    showArchived: { type: Boolean, default: false },
  },

  data() {
    return {
      perPage: 10,
      currentPage: 1,
      search: '',

      isLoading: false,
      isAddingProject: false,

      fields: [
        {
          key: 'name',
          label: 'Project Name',
          class: 'w-25',
          sortable: true,
        },
        {
          key: 'tradies_count',
          label: 'Tradies',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'tradies_need_attention_count',
          label: 'Tradies Needing Attention',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'project_approved_tradies_count',
          label: 'Project Approved Tradies',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
          formatter: (value) => (value == null ? 0 : value),
        },
        {
          key: 'action',
          label: 'Action',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
      ],

      archivedVariable: true,
      cellVariant: 'danger',
      cellText: 'Archive',

      sharedProjectIds: [],
    }
  },

  created() {
    this.setArchiveVariables()
    if (!this.isProjectScoped) {
      this.fetchActiveShareProjectIds()
    }
  },

  computed: {
    ...mapGetters('project', ['projects', 'projectsMeta', 'project']),
    ...mapGetters('auth', ['user', 'hasPermission', 'isProjectScoped']),

    isBusinessAdmin() {
      return !!(this.user && this.user.user_type === 'business_admin')
    },

    rows() {
      return this.projectsMeta ? this.projectsMeta.total : 0
    },

    sharedProjectIdSet() {
      return new Set(this.sharedProjectIds)
    },

    canViewProjectDetail() {
      if (
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
        return true
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.view')
    },

    canCreateProject() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.create')
    },

    canExportMatrix() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.export')
    },

    canArchiveProject() {
      if (!this.isBusinessAdmin) return false
      return this.hasPermission('projects.archive')
    },
  },

  watch: {
    currentPage: async function (newCurrentPage) {
      await this.fetchProjects(newCurrentPage, this.perPage, this.search)
    },

    search: async function (newSearch) {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        await this.fetchProjects(1, this.perPage, newSearch)
      }
    },
  },

  methods: {
    ...mapActions('project', {
      getProjects: 'getProjects',
      getProject: 'getProject',
      createProject: 'createProject',
      deleteProject: 'deleteProject',
      updateProject: 'updateProject',
    }),

    ...mapMutations('project', {
      setProject: 'setProject',
    }),

    async fetchProjects(page, limit, search) {
      this.isLoading = true

      await this.getProjects({
        page,
        limit,
        search,
        is_archived: this.showArchived,
      }).finally(() => {
        this.isLoading = false
      })
    },

    async viewProject(item) {
      if (!this.canViewProjectDetail) return
      this.$router.push({
        name: 'projects.view-page',
        params: {
          id: item.id,
        },
      })
    },

    isShareLocked(project) {
      if (!project || this.showArchived) return false
      return this.sharedProjectIdSet.has(project.id)
    },

    refreshShareLocks() {
      if (this.isProjectScoped) return
      return this.fetchActiveShareProjectIds()
    },

    async fetchActiveShareProjectIds() {
      try {
        const { data } = await this.$api.get(
          '/project-shares?direction=outgoing&limit=200',
        )
        const items = (data && data.data) || []
        const now = Date.now()
        this.sharedProjectIds = items
          .filter((s) => {
            if (!s || !s.project || s.project.id == null) return false
            if (!s.expires_at) return true
            return new Date(s.expires_at).getTime() > now
          })
          .map((s) => s.project.id)
      } catch (err) {
        this.sharedProjectIds = []
      }
    },

    async archiveProject(project) {
      if (this.isShareLocked(project)) return
      const ok = await this.$bvModal.msgBoxConfirm(
        `${this.cellText} ${project.name}?`,
        {
          title: `${this.cellText} Project`,
          okTitle: 'Yes',
          cancelTitle: 'No',
          okVariant: 'danger',
          centered: true,
          footerClass: 'archive-confirm-footer',
        },
      )
      if (!ok) return
      await this.updateProject({
        id: project.id,
        name: project.name,
        is_archived: this.archivedVariable,
      })
      this.fetchProjects(this.currentPage, this.perPage, this.search)
    },

    setArchiveVariables() {
      if (this.showArchived) {
        this.archivedVariable = false
        this.cellVariant = 'success'
        this.cellText = 'Unarchive'
      }
    },

    addingProject(status) {
      this.resetForm()
      this.isAddingProject = status
    },

    // Plant with no project scope applies to every project, new ones included.
    // Returns true to keep that, false to opt this project out, null to abort.
    async askApplyAllProjectsPlant() {
      let count = 0
      try {
        const { data } = await this.$api.get('/plant-equipment')
        count = ((data && data.data) || []).filter(
          (item) => item.applies_to_all_projects,
        ).length
      } catch (err) {
        return true
      }
      if (!count) return true

      const noun = count === 1 ? 'item' : 'items'
      return this.$bvModal.msgBoxConfirm(
        `You have ${count} plant / equipment ${noun} applied to all projects. Apply ${
          count === 1 ? 'it' : 'them'
        } to this new project too?`,
        {
          title: 'Plant / Equipment',
          okTitle: 'Yes, apply',
          cancelTitle: "No, don't apply",
          centered: true,
        },
      )
    },

    async onAddProject() {
      const applyPlant = await this.askApplyAllProjectsPlant()
      if (applyPlant === null) return

      await this.createProject(
        applyPlant
          ? this.project
          : { ...this.project, exclude_all_projects_plant: true },
      )
        .then((res) => {
          this.$bvToast.toast('Project created successfully!', {
            title: 'Success',
            autoHideDelay: 3000,
            variant: 'success',
          })
        })
        .catch((err) => this.handleError(err))
        .finally(() => {
          this.addingProject(false)
          this.fetchProjects(this.currentPage, this.perPage, this.search)
        })
    },

    resetForm() {
      this.setProject({ name: '' })
      this.searchRequiredFiles = ''
      this.selectedTemplateIds = []
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },

    handleError(err) {
      this.showToast('Oops.. Something went wrong!', err.data.message, 'danger')
    },

    async downloadAllTicketsCsvSquashed() {
      try {
        this.isLoading = true

        const response = await this.$api.get(
          '/projects/tradie-tickets-csv-squashed',
          {
            responseType: 'blob',
          },
        )

        // Zero exportable rows (e.g. every file is still pending malware scan)
        // comes back as a JSON message instead of a near-empty CSV.
        const contentType =
          (response.headers && response.headers['content-type']) || ''
        if (contentType.includes('application/json')) {
          const text = await response.data.text()
          const { message } = JSON.parse(text)
          this.showToast(
            'Nothing to export',
            message || 'No files available to export.',
            'warning',
          )
          return
        }

        const filename = 'All_Projects_Tradie_Tickets.csv'

        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.showToast('Success', 'CSV download started.')
      } catch (err) {
        this.showToast('Error', 'Failed to download CSV', 'danger')
      } finally {
        this.isLoading = false
      }
    },
  },

  async beforeMount() {
    this.resetForm()
  },

  async mounted() {
    await this.fetchProjects(this.currentPage, this.perPage, this.search)
  },
}
</script>

<style lang="scss" scoped>
/deep/ .cursor-pointer {
  cursor: pointer;
}

/* Desktop toolbar buttons (Add New Project / Export Tradie Matrix) — a touch
   shorter than the app-wide 56px .btn height. */
.action-btn {
  height: 53px;
  padding-top: 0;
  padding-bottom: 0;
}

/* Add New Project modal actions: a consistent pair — right-aligned with a set
   min-width on larger screens, and equal full-width halves on phones.
   justify-content-end used to be set as an inline Bootstrap utility class,
   which carries !important and silently blocked the "stretch" override
   below — set it here instead so our own small-screen rule can win. */
.add-project-actions {
  gap: 0.5rem;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}
.modal-btn {
  min-width: 110px;
  font-weight: 600;
}

@media (max-width: 575.98px) {
  .add-project-actions {
    justify-content: stretch;
    margin-bottom: 1rem;
  }
  .modal-btn {
    flex: 1 1 0;
    min-width: 0;
    height: 44px;
  }

  /* Match the Expiring Soon table's compact pagination on phones so both
     home-page tables' pagination looks the same size/style. */
  /deep/ .pagination {
    font-size: 0.75rem;

    .page-link {
      padding: 0.25rem 0.5rem;
    }
  }
}

.mobile-project-item {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
}

.mobile-project__name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-project__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  line-height: 1.4;
}

.project-mobile-btn {
  align-self: stretch;
  height: auto;
  width: 42px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
}

/* The two 42px buttons + gaps eat most of the row's width on narrow phones,
   leaving too little room for the placeholder at default size — shrink the
   text and padding so it reads fully instead of clipping. */
@media (max-width: 400px) {
  .project-mobile-search {
    font-size: 0.82rem;
    padding-left: 0.6rem;
    padding-right: 0.6rem;
  }
}

.mobile-project__archive-btn {
  height: 36px;
  width: 36px;
  padding: 0;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
}
</style>

<!-- Non-scoped: the "Confirm" (archive/unarchive) dialog renders at the body
     level, so its footer must be styled globally. Match the Request renewal
     dialog's compact, spread, equal-width phone buttons. -->
<style>
.archive-confirm-footer {
  flex-wrap: nowrap;
  gap: 0.5rem;
}
@media (max-width: 575.98px) {
  .archive-confirm-footer > .btn {
    flex: 1 1 0;
    min-width: 0;
    height: 44px;
  }
}
</style>
